import { CalendarDate } from '@internationalized/date';
import {
  FreightTerm,
  PaymentTerm,
  enumUnit,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { validateLineItems } from '../_components/purchase-orders/helpers/validateLineItems';
import { purchaseOrderCreateAtom } from '../_graphql/mutationPurchaseOrderCreate';
import { purchaseOrderGetNextDocumentNumberAtom } from '../_graphql/mutationPurchaseOrderGetNextDocumentNumber';
import { purchaseOrderUpdateAtom } from '../_graphql/mutationPurchaseOrderUpdate';
import { purchaseOrderDetailQueryAtom } from '../_graphql/queryPurchaseOrderDetail';
import { PurchaseOrderLineItemRecord } from '../types/purchaseOrder';
import { calculateTotalPrice } from '../utils/money';

export type PurchaseOrderFormState = {
  id: string | null;
  // Contract Info
  facilityId: string | null;
  purchaseOrderStartDate: CalendarDate | null;
  purchaseOrderEndDate: CalendarDate | null;
  purchaseOrderNumber: string;
  accountRepresentativeId: string | null;
  counterpartyReferenceNumber: string;

  // Supplier Information
  supplierId: string | null;
  contactId: string | null;
  supplierAddressId: string | null;

  // Payment Info
  paymentCurrency: string | null;
  paymentTerm: PaymentTerm | null;
  freightTerm: FreightTerm | null;

  // Materials
  lineItems: PurchaseOrderLineItemRecord[];

  // Other Info
  notes: string;
  completionNotes: string;

  // Form state
  isSubmitting: boolean;
  errors: Record<string, string>;
};

const initialPurchaseOrderFormState: PurchaseOrderFormState = {
  id: null,
  // Contract Info
  facilityId: null,
  purchaseOrderStartDate: null,
  purchaseOrderEndDate: null,
  purchaseOrderNumber: '',
  accountRepresentativeId: null,
  counterpartyReferenceNumber: '',

  // Supplier Information
  supplierId: null,
  contactId: null,
  supplierAddressId: null,

  // Payment Info
  paymentCurrency: null,
  paymentTerm: null,
  freightTerm: null,

  // Materials
  lineItems: [],

  // Other Info
  notes: '',
  completionNotes: '',

  // Form state
  isSubmitting: false,
  errors: {},
};

// Persistent storage for draft purchase order forms by ID
export const $draftPurchaseOrderForms = atomWithStorage<
  Map<string, PurchaseOrderFormState>
>('purchase-order-draft-forms', new Map(), {
  getItem: (key) => {
    const stored = localStorage.getItem(key);
    if (!stored) return new Map();
    try {
      const parsed = JSON.parse(stored);

      const reconstructedEntries: [string, PurchaseOrderFormState][] = (
        Object.entries(parsed) as [string, PurchaseOrderFormState][]
      ).map(([id, form]) => [
        id,
        {
          ...form,
          purchaseOrderStartDate: form.purchaseOrderStartDate
            ? new CalendarDate(
                form.purchaseOrderStartDate.year,
                form.purchaseOrderStartDate.month,
                form.purchaseOrderStartDate.day,
              )
            : null,
          purchaseOrderEndDate: form.purchaseOrderEndDate
            ? new CalendarDate(
                form.purchaseOrderEndDate.year,
                form.purchaseOrderEndDate.month,
                form.purchaseOrderEndDate.day,
              )
            : null,
        },
      ]);

      return new Map(reconstructedEntries);
    } catch (error) {
      console.error(
        'Failed to parse localStorage purchase order forms:',
        error,
      );
      return new Map();
    }
  },
  setItem: (key, value) => {
    const obj = Object.fromEntries(
      Array.from(value.entries()).map(([id, form]) => [
        id,
        {
          ...form,
          purchaseOrderStartDate: form.purchaseOrderStartDate
            ? {
                calendar: form.purchaseOrderStartDate.calendar,
                era: form.purchaseOrderStartDate.era,
                year: form.purchaseOrderStartDate.year,
                month: form.purchaseOrderStartDate.month,
                day: form.purchaseOrderStartDate.day,
              }
            : null,
          purchaseOrderEndDate: form.purchaseOrderEndDate
            ? {
                calendar: form.purchaseOrderEndDate.calendar,
                era: form.purchaseOrderEndDate.era,
                year: form.purchaseOrderEndDate.year,
                month: form.purchaseOrderEndDate.month,
                day: form.purchaseOrderEndDate.day,
              }
            : null,
        },
      ]),
    );

    const jsonString = JSON.stringify(obj);
    localStorage.setItem(key, jsonString);
  },
  removeItem: (key) => localStorage.removeItem(key),
});

export const $currentStep = atom<number>(1);
export const $setCurrentStep = atom(null, (get, set, step: number) => {
  set($currentStep, step);
});

export const $resetCurrentStep = atom(null, (get, set) => {
  set($currentStep, 1);
});

const $currentDraftIdInternal = atom<string | null>(null);

export const $currentDraftId = atom(
  (get) => get($currentDraftIdInternal),
  (get, set, newValue: string | null) => set($currentDraftIdInternal, newValue),
);

export const $purchaseOrderForm = atom<PurchaseOrderFormState>((get) => {
  const drafts = get($draftPurchaseOrderForms);
  const currentId = get($currentDraftId);

  if (currentId && drafts.has(currentId)) {
    return drafts.get(currentId) ?? initialPurchaseOrderFormState;
  }

  return initialPurchaseOrderFormState;
});

export const $updatePurchaseOrderFormState = atom(
  null,
  (get, set, updates: Partial<PurchaseOrderFormState>) => {
    const currentForm = get($purchaseOrderForm);
    const newForm = { ...currentForm, ...updates };

    if (newForm.id) {
      const drafts = get($draftPurchaseOrderForms);
      const newDrafts = new Map(drafts);
      newDrafts.set(newForm.id, newForm);
      set($draftPurchaseOrderForms, newDrafts);
    }

    if (updates.id !== undefined) {
      set($currentDraftId, updates.id);
    }
  },
);

export const $updatePurchaseOrderForm = atom(
  null,
  (get, set, updates: Partial<PurchaseOrderFormState>) => {
    set($updatePurchaseOrderFormState, updates);
  },
);

export const $setPurchaseOrderForm = atom(
  null,
  (get, set, formState: Partial<PurchaseOrderFormState>) => {
    set($updatePurchaseOrderFormState, formState);
  },
);

export const $setPurchaseOrderId = atom(null, (get, set, id: string | null) => {
  set($updatePurchaseOrderFormState, { id });
});

export const $resetPurchaseOrderForm = atom(null, (get, set) => {
  set($currentDraftId, null);
  set($currentStep, 1);
});

export const PURCHASE_ORDER_REQUIRED_FIELDS = [
  'purchaseOrderNumber',
  'facilityId',
  'purchaseOrderStartDate',
  'supplierId',
  'supplierAddressId',
  'paymentCurrency',
  'paymentTerm',
  'freightTerm',
] as const;

export type PurchaseOrderRequiredField =
  (typeof PURCHASE_ORDER_REQUIRED_FIELDS)[number];

type FieldValidator = (form: PurchaseOrderFormState) => boolean;

const fieldValidators: Record<PurchaseOrderRequiredField, FieldValidator> = {
  purchaseOrderNumber: (form) => !form.purchaseOrderNumber.trim(),
  facilityId: (form) => !form.facilityId,
  purchaseOrderStartDate: (form) => !form.purchaseOrderStartDate,
  supplierId: (form) => !form.supplierId,
  supplierAddressId: (form) => !form.supplierAddressId,
  paymentCurrency: (form) => !form.paymentCurrency,
  paymentTerm: (form) => !form.paymentTerm,
  freightTerm: (form) => !form.freightTerm,
};

const fieldErrorMessages: Record<PurchaseOrderRequiredField, string> = {
  purchaseOrderNumber: 'Purchase order number is required',
  facilityId: 'Facility is required',
  purchaseOrderStartDate: 'Start date is required',
  supplierId: 'Supplier is required',
  supplierAddressId: 'Supplier address is required',
  paymentCurrency: 'Payment currency is required',
  paymentTerm: 'Payment term is required',
  freightTerm: 'Freight term is required',
};

const getMissingRequiredFields = (
  form: PurchaseOrderFormState,
): PurchaseOrderRequiredField[] =>
  PURCHASE_ORDER_REQUIRED_FIELDS.filter((field) =>
    fieldValidators[field](form),
  );

export const $validatePurchaseOrderForm = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($purchaseOrderForm);

    const missingFields = getMissingRequiredFields(form);
    const requiredErrors = missingFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: fieldErrorMessages[field],
      }),
      {} as Record<string, string>,
    );

    let errors = { ...requiredErrors };

    if (get($currentStep) === 2) {
      const lineItemErrors = validateLineItems(form.lineItems);
      errors = { ...errors, ...lineItemErrors };
    }

    set($updatePurchaseOrderFormState, { errors });
    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

export const $purchaseOrderMissingRequiredFields = atom((get) => {
  const form = get($purchaseOrderForm);
  return getMissingRequiredFields(form);
});

export const $purchaseOrderIsMissingRequired = atom(
  (get) => get($purchaseOrderMissingRequiredFields).length > 0,
);

export const $validatePurchaseOrderFormDraft = atom(
  null,
  (get, set): { isValid: boolean; errors: Record<string, string> } => {
    const form = get($purchaseOrderForm);
    let errors: Record<string, string> = {};

    if (!form.purchaseOrderNumber.trim()) {
      errors.purchaseOrderNumber = 'Purchase order number is required';
    }

    if (!form.supplierId) {
      errors.supplierId = 'Supplier is required';
    }

    if (get($currentStep) === 2) {
      const lineItemErrors = validateLineItems(form.lineItems);
      errors = { ...errors, ...lineItemErrors };
      set($updatePurchaseOrderFormState, { errors });
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  },
);

export const $addLineItem = atom(null, (get, set) => {
  const form = get($purchaseOrderForm);
  const newItem: PurchaseOrderLineItemRecord = {
    id: `temp-${Date.now()}-${Math.random()}`,
    unit: enumUnit.Pounds,
    quantity: null,
    unitPrice: null,
    totalPrice: null,
    itemId: null,
  };

  set($updatePurchaseOrderFormState, {
    lineItems: [...form.lineItems, newItem],
  });
});

export const $updateLineItem = atom(
  null,
  (
    get,
    set,
    {
      index,
      updates,
    }: { index: number; updates: Partial<PurchaseOrderLineItemRecord> },
  ) => {
    const form = get($purchaseOrderForm);
    const updatedItems = [...form.lineItems];
    const currentItem = updatedItems[index];

    const updatedItem = { ...currentItem, ...updates };

    if (updates.quantity !== undefined || updates.unitPrice !== undefined) {
      updatedItem.totalPrice = calculateTotalPrice(
        updatedItem.unitPrice,
        updatedItem.quantity,
      );
    }

    updatedItems[index] = updatedItem;

    set($updatePurchaseOrderFormState, {
      lineItems: updatedItems,
    });
  },
);

export const $removeLineItem = atom(null, (get, set, index: number) => {
  const form = get($purchaseOrderForm);
  const updatedItems = form.lineItems.filter((_, i) => i !== index);

  set($updatePurchaseOrderFormState, {
    lineItems: updatedItems,
  });
});

export const $purchaseOrderFormIsValid = atom((get) => {
  const form = get($purchaseOrderForm);
  return Object.keys(form.errors).length === 0;
});

export const $existingPurchaseOrder = purchaseOrderDetailQueryAtom((get) => ({
  variables: {
    id: get($purchaseOrderForm).id ?? '',
  },
  skip:
    !get($purchaseOrderForm).id ||
    get($purchaseOrderForm)?.id?.startsWith('temp-'),
}));

export const $createPurchaseOrder = purchaseOrderCreateAtom();

export const $updatePurchaseOrder = purchaseOrderUpdateAtom();

export const $nextDocumentNumber = purchaseOrderGetNextDocumentNumberAtom();
